import { useQuery } from '@apollo/client';
import { StackShim as Stack, StackItemShim as StackItem } from '@fluentui/react-migration-v8-v9';
import { ITag } from '@fluentui/react';
import { Button, Divider, Field, InfoLabel, Switch, makeStyles } from '@fluentui/react-components';
import React from 'react';
import { dividerStyles } from '../../../app/common/styles/CommonStyleObjects';
import { PROCESS_TYPES } from '../../../utils/statApi/ProcessTypesApi';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import { multiDropdownMediumWrapper } from '../../companies/documents/CompanyDocumentStyles';
import FilterWwAreaPicker from '../../companies/list/FilterWwAreaPicker';
import { IProcessSearchState } from './IProcessSearchResult';
import ProcessSearchCompanyPicker from './ProcessSearchCompanyPicker';
import ProcessSearchCountryPicker from './ProcessSearchCountryPicker';
import ProcessSearchFiscalYearPicker from './ProcessSearchFiscalYearPicker';
import DateRangePicker from './DateRangePicker';
import MessageWithTooltip from '../../common/MessageWithToolTip';
import OPS_DETAILS from '../../../utils/statApi/OpsHierarchyApi';
import MultiSelectDropdown from '../../common/MultiSelectDropdown';
import IDropdownOption from '../../../utils/types/IDropdownOption';

interface IProcessSearchFilterProps {
  onResetFiltersClick: () => void;
  filterState: IProcessSearchState;
  onUpdateState: (newState: IProcessSearchState) => void;
  showFcwFilters?: boolean;
}
const useStyles = makeStyles({
  filterPanel: { width: '300px' },
});

const ProcessSearchFilter = (props: IProcessSearchFilterProps): JSX.Element => {
  const styles = dividerStyles();
  const classes = useStyles();
  const { onResetFiltersClick, filterState, onUpdateState, showFcwFilters } = props;

  const {
    companyCodes,
    processTypeIds,
    processStatuses,
    readyMilestoneStatuses,
    reportingPeriodEndFromDate,
    reportingPeriodEndToDate,
    filingDueFromDate,
    filingDueToDate,
    countryCodes,
    selectedFilingDueDateOption,
    selectedPeriodEndDateOption,
    companyAreaCodes,
    showMyItems,
    milestoneDueDateStartDate,
    milestoneDueDateEndDate,
    opsDetailIds,
  } = filterState;
  const {
    data: processTypes,
    loading: processTypesLoading,
    error: processTypesError,
  } = useQuery(PROCESS_TYPES);

  const {
    data: opsDetailsData,
    loading: opsDetailsLoading,
    error: opsDetailsError,
  } = useQuery(OPS_DETAILS);

  const onFieldChange = (
    propertyName: keyof IProcessSearchState,
    value: number[] | string[] | Date | boolean,
  ) => {
    const newState = { ...filterState, [propertyName]: value };
    onUpdateState(newState);
  };

  const onProcessTypeUpdate = (
    _event: React.FormEvent<HTMLElement>,
    items: IDropdownOption[],
  ): void => {
    const itemIds = items.map((item) => Number(item.key));
    onFieldChange('processTypeIds', itemIds);
  };

  const onMilestoneReadyStatusUpdate = (
    _event: React.FormEvent<HTMLElement>,
    items: IDropdownOption[],
  ): void => {
    const itemKeys = items.map((item) => item.key as string);
    onFieldChange('readyMilestoneStatuses', itemKeys);
  };

  const onProcessStatusUpdate = (
    _event: React.FormEvent<HTMLElement>,
    items: IDropdownOption[],
  ): void => {
    const itemKeys = items.map((item) => item.key as string);
    onFieldChange('processStatuses', itemKeys);
  };

  const onOpsDetailsUpdate = (
    _event: React.FormEvent<HTMLElement>,
    items: IDropdownOption[],
  ): void => {
    const itemIds = items.map((item) => Number(item.key));
    onFieldChange('opsDetailIds', itemIds);
  };

  const onReportingPeriodDateRangeChange = (
    newStartDate: Date,
    newEndDate: Date,
    newSelectedOption: string,
  ) => {
    const newReportingEndDateRange = {
      reportingPeriodEndFromDate: newStartDate,
      reportingPeriodEndToDate: newEndDate,
      selectedPeriodEndDateOption: newSelectedOption,
    };
    const newState = { ...filterState, ...newReportingEndDateRange };
    onUpdateState(newState);
  };
  const onFilingDueDateRangeChange = (
    newStartDate: Date,
    newEndDate: Date,
    newSelectedOption: string,
  ) => {
    const newReportingEndDateRange = {
      filingDueFromDate: newStartDate,
      filingDueToDate: newEndDate,
      selectedFilingDueDateOption: newSelectedOption,
    };
    const newState = { ...filterState, ...newReportingEndDateRange };
    onUpdateState(newState);
  };

  const onMilestoneDueDateRangeChange = (newStartDate: string, newEndDate: string) => {
    const newMilestoneDateRange = {
      // This filter always needs to have both a start and end to the range
      milestoneDueDateStartDate: newStartDate === null ? newEndDate : newStartDate,
      milestoneDueDateEndDate: newEndDate === null ? newStartDate : newEndDate,
    };
    const newState = { ...filterState, ...newMilestoneDateRange };
    onUpdateState(newState);
  };

  const onMyItemsChange = (ev: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
    onFieldChange('showMyItems', data.checked);
  };

  const processStatusOptions = [
    { key: 'COMPLETE', text: 'Complete' },
    { key: 'ACTIVE', text: 'Active ' },
  ];
  const readyMilestoneStatusOptions = [
    { key: 'OVERDUE', text: 'Overdue' },
    { key: 'UPCOMING', text: 'Upcoming' },
    { key: 'ONTRACK', text: 'On-Track' },
  ];

  return (
    <StackItem grow disableShrink className={classes.filterPanel}>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <h2>Filter</h2>
        <Button onClick={onResetFiltersClick}>Reset Filters</Button>
      </Stack>
      <ProcessSearchCompanyPicker
        initialSelectedCodes={companyCodes}
        onCompanyUpdate={(items: ITag[]) => {
          onFieldChange(
            'companyCodes',
            items.map((item) => item.name.split(' ')[0].toString()),
          );
        }}
      />
      <Divider className={styles.divider} />
      <ProcessSearchCountryPicker
        initialSelectedCodes={countryCodes}
        onCountryUpdate={(items: ITag[]) => {
          onFieldChange(
            'countryCodes',
            items.map((item) => item.key.toString()),
          );
        }}
      />
      <Divider className={styles.divider} />
      <FilterWwAreaPicker
        initialSelectedCodes={companyAreaCodes}
        onRegionAreaUpdate={(items: ITag[]) => {
          onFieldChange(
            'companyAreaCodes',
            items.map((item) => item.key.toString()),
          );
        }}
      />
      <Divider className={styles.divider} />
      <ProcessSearchFiscalYearPicker
        label="Reporting Period End Date"
        initialStartDate={reportingPeriodEndFromDate}
        initialEndDate={reportingPeriodEndToDate}
        onUpdateDate={(startDate: Date, endDate: Date, initialSelectedOption: string) => {
          onReportingPeriodDateRangeChange(startDate, endDate, initialSelectedOption);
        }}
        initialSelectedOption={selectedPeriodEndDateOption}
      />
      <Divider className={styles.divider} />
      <ProcessSearchFiscalYearPicker
        label="Filing Due Date"
        initialStartDate={filingDueFromDate}
        initialEndDate={filingDueToDate}
        onUpdateDate={(startDate: Date, endDate: Date, initialSelectedOption: string) => {
          onFilingDueDateRangeChange(startDate, endDate, initialSelectedOption);
        }}
        initialSelectedOption={selectedFilingDueDateOption}
      />
      <Divider className={styles.divider} />
      <MultiSelectDropdown
        label="Process Status"
        className={multiDropdownMediumWrapper}
        onChange={onProcessStatusUpdate}
        selectedKeys={processStatuses}
        placeholder="Select an option"
        options={processStatusOptions}
      />
      <Divider className={styles.divider} />
      <LoadingErrorMessage loading={processTypesLoading} error={processTypesError} />
      <MultiSelectDropdown
        label="Process Type"
        className={multiDropdownMediumWrapper}
        onChange={onProcessTypeUpdate}
        selectedKeys={processTypeIds?.map((id) => id.toString()) ?? []}
        placeholder="Select an option"
        options={processTypes?.allProcessTypes ?? []}
      />
      {showFcwFilters && (
        <>
          <Divider className={styles.divider} />
          <LoadingErrorMessage loading={opsDetailsLoading} error={opsDetailsError} />
          <MultiSelectDropdown
            label="Ops Org Detail"
            className={multiDropdownMediumWrapper}
            onChange={onOpsDetailsUpdate}
            selectedKeys={opsDetailIds?.map((id) => id.toString()) ?? []}
            placeholder="Select an option"
            options={opsDetailsData?.opsDetails ?? []}
          />
          <Divider className={styles.divider} />
          <Field
            label={
              <InfoLabel info="Shows any process with at least one milestone where you are an assignee">
                My Items Only
              </InfoLabel>
            }
          >
            <Switch checked={showMyItems} onChange={onMyItemsChange} />
          </Field>
          <Divider className={styles.divider} />
          <DateRangePicker
            label={
              <MessageWithTooltip
                label="Milestone Due Date"
                tooltipMessage="Show any process with at least one milestone's due date within the selected range"
              />
            }
            initialStartDate={milestoneDueDateStartDate}
            initialEndDate={milestoneDueDateEndDate}
            onUpdateDate={(startDate: string, endDate: string) => {
              onMilestoneDueDateRangeChange(startDate, endDate);
            }}
          />
          <Divider className={styles.divider} />
          <MultiSelectDropdown
            label="Ready Milestone Status"
            tooltipMessage="Shows any process where the ready milestone has the following status(es)."
            className={multiDropdownMediumWrapper}
            onChange={onMilestoneReadyStatusUpdate}
            selectedKeys={readyMilestoneStatuses}
            placeholder="Select an option"
            options={readyMilestoneStatusOptions}
          />
        </>
      )}
    </StackItem>
  );
};
export default ProcessSearchFilter;
